import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import translate from "../../../i18n/translate";
import ApiRequest from "../../../actions/ApiRequest";
import Toast from "../../common/Toast";
import EPRSProductsTable from "./EPRSProductsTable";

// Runs the EPRS product sync and shows it happening. The sync replaces every
// EPRS product across all operators, takes minutes, and runs on the server
// in the background; this page polls its progress every two seconds, so it
// also shows a run another admin started.
const POLL_MS = 2000;

const formatElapsed = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
};

const EPRSSettings = ({ isDarktheme }) => {
  const [sync, setSync] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  const readStatus = async () => {
    try {
      const res = await ApiRequest.post("eprs_update_products?operation=status");
      if (res?.data?.status === "success") setSync(res.data.sync);
    } catch (err) {
      // the axios interceptor already reports the failure
    }
  };

  useEffect(() => {
    readStatus();
    return () => clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (sync?.running) timer.current = setTimeout(readStatus, POLL_MS);
  }, [sync]);

  const start = async () => {
    setStarting(true);
    try {
      const res = await ApiRequest.post("eprs_update_products?operation=start");
      if (res?.data?.status === "success") {
        setSync(res.data.sync);
        if (!res.data.started) Toast.fire({ title: res.data.reason, icon: "info" });
      } else {
        Toast.fire({ title: res?.data?.reason || "Something Went Wrong!", icon: "error" });
      }
    } catch (err) {
      Toast.fire({ title: "Something Went Wrong!", icon: "error" });
    } finally {
      setStarting(false);
    }
  };

  const running = Boolean(sync?.running);
  const percent = sync?.total ? Math.round((sync.done / sync.total) * 100) : 0;
  const finished = sync && !sync.running && sync.finished;

  return (
    <Box sx={{ padding: 3, color: isDarktheme ? "#fff" : "inherit" }}>
      <Typography variant="body2" sx={{ marginBottom: 3, opacity: 0.75, maxWidth: "62ch" }}>
        {translate("eprsSyncHint")}
      </Typography>

      <Paper sx={{ padding: 3, maxWidth: 720, marginBottom: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Button variant="contained" onClick={start} disabled={starting || running}>
            {starting ? <CircularProgress size={20} color="inherit" /> : translate("Update Products")}
          </Button>
          {running && (
            <Chip
              size="small"
              color="primary"
              label={<>{translate("Updating")} · {formatElapsed(sync.elapsed_seconds)}</>}
            />
          )}
          {finished && (
            <Chip
              size="small"
              color={sync.ok ? "success" : "error"}
              label={<>{translate("Last run")}: {sync.finished}</>}
            />
          )}
        </Box>

        {running && (
          <Box sx={{ marginTop: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: 0.5 }}>
              <Typography variant="body2">
                {sync.total
                  ? `${sync.current} (${sync.done} / ${sync.total})`
                  : translate("Fetching the operator list")}
              </Typography>
              <Typography variant="body2">{percent}%</Typography>
            </Box>
            <LinearProgress variant={sync.total ? "determinate" : "indeterminate"} value={percent} />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {translate("Products written")}: {sync.inserted}
            </Typography>
          </Box>
        )}

        {finished && (
          <Alert severity={sync.ok ? "success" : "error"} sx={{ marginTop: 3 }}>
            {sync.ok ? (
              <>
                {sync.inserted} {translate("products written for")} {sync.done} {translate("operators in")}{" "}
                {formatElapsed(sync.elapsed_seconds)}.
              </>
            ) : (
              sync.error || translate("The sync did not complete.")
            )}
          </Alert>
        )}
      </Paper>

      {sync?.operators?.length > 0 && (
        <Paper sx={{ maxWidth: 720 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate("Operator")}</TableCell>
                <TableCell>{translate("Category")}</TableCell>
                <TableCell align="right">{translate("Products")}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sync.operators.map((op, i) => (
                <TableRow key={i}>
                  <TableCell>{op.name}</TableCell>
                  <TableCell>{op.category}</TableCell>
                  <TableCell align="right">{op.products}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" color={op.ok ? "success" : "error"} label={op.ok ? "✓" : "✗"} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <EPRSProductsTable refreshKey={sync?.finished || ""} />
    </Box>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({ isDarktheme });

export default connect(mapStateToProps, null)(EPRSSettings);
